import h from 'hyperscript';

import designerIcon from '@disy/cadenza-icons/designer.svg';
import { assertUnreachable } from '@disy/lib/custom-error';
import { on } from '@disy/lib/event-util';
import { icon } from '@disy/lib/icon';
import { logger } from '@disy/lib/logging';
import { type Teardown, tearDown } from '@disy/lib/teardown';
import { POPUP_PARENT_MARKER_CLASS } from '@disy/ui/popup';

import { isActiveWorksheetOnDemand } from 'cadenza/map/on-demand/on-demand-map.state';
import { getStore } from 'cadenza/store/cadenza-store';
import { observeStore } from 'cadenza/store/state-utils';
import type { DataViewVisualizationKey } from 'cadenza/workbook/api/data-view-visualization-key';
import type { ViewId } from 'cadenza/workbook/api/view-id';
import { ViewType } from 'cadenza/workbook/api/view-type';
import { COMMAND_UPDATE } from 'cadenza/workbook/event-types';
import { isPanelVisible } from 'cadenza/workbook/state/workbook-panel.state';
import { isTextWithDataView } from 'cadenza/workbook/view/legacy-text/text-with-data-utils';
import type { View } from 'cadenza/workbook/view/view';
import {
  getActiveViewId,
  getActiveViewType,
  getInitializationStatus,
  getVisualisationKeyByViewId,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type {
  DesignerSettingsPanelContent,
  ViewSettingsPanelContentPromiseFunction,
} from 'cadenza/workbook/workbook-panel/settings/designer-settings-panel-content';
import { getExtensionViewsSettingsPanelContent } from 'cadenza/workbook/workbook-panel/settings/other-views/extension-view-settings-panel-content-provider';
import { WorksheetSettingsPanel } from 'cadenza/workbook/workbook-panel/settings/worksheet/worksheet-settings-panel/worksheet-settings-panel';
import { WorksheetTocPanel } from 'cadenza/workbook/workbook-panel/settings/worksheet/worksheet-toc-panel/worksheet-toc-panel';
import { WorkbookPanel } from 'cadenza/workbook/workbook-panel/workbook-panel';
import { WorkbookPanelName } from 'cadenza/workbook/workbook-panel/workbook-panel-utils';

import './view-settings-panel.css';
import i18n from './view-settings-panel.properties';

// dynamic imports
const getTableViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/table/table-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getMapViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/map/map-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getChartViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/chart/chart-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getTextViewSettingsPanelContent = (view: View) =>
  isTextWithDataView(view) ?
    (import(
      'cadenza/workbook/workbook-panel/settings/text/text-with-data-view-settings-panel-content'
    ) as unknown as ViewSettingsPanelContentPromiseFunction)
  : (import(
      'cadenza/workbook/workbook-panel/settings/text/text-view-settings-panel-content'
    ) as unknown as ViewSettingsPanelContentPromiseFunction);
const getImageViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/image/image-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getReportViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/report/report-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getControllerViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/controller/controller-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getWebpageViewSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/webpage/webpage-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;
const getOtherViewsSettingsPanelContent = () =>
  import(
    'cadenza/workbook/workbook-panel/settings/other-views/indicator-view-settings-panel-content'
  ) as unknown as ViewSettingsPanelContentPromiseFunction;

const COMPONENT_NAME = WorkbookPanelName.VIEW_SETTINGS;
const TITLE_ID = `${COMPONENT_NAME}-title`;

const CONTENT_PROVIDERS_BY_VIEW_TYPE = new Map<ViewType, (view: View) => ViewSettingsPanelContentPromiseFunction>([
  [ViewType.TABLE, getTableViewSettingsPanelContent],
  [ViewType.MAP, getMapViewSettingsPanelContent],
  [ViewType.CHART, getChartViewSettingsPanelContent],
  [ViewType.TEXT, getTextViewSettingsPanelContent],
  [ViewType.IMAGE, getImageViewSettingsPanelContent],
  [ViewType.OTHER, getOtherViewsSettingsPanelContent],
  [ViewType.CONTROLLER, getControllerViewSettingsPanelContent],
  [ViewType.WEBPAGE, getWebpageViewSettingsPanelContent],
  [ViewType.REPORT, getReportViewSettingsPanelContent],
  [ViewType.EXTENSION, getExtensionViewsSettingsPanelContent],
]);

export class ViewSettingsPanel extends WorkbookPanel {
  #loadPanelContentPromise?: Promise<HTMLElement | DesignerSettingsPanelContent>;
  #shownViewId?: ViewId; // Used for refresh analysis
  #shownViewType?: ViewType;
  #shownVisualisationId?: DataViewVisualizationKey;
  #teardowns: Teardown[] = [];

  constructor() {
    super(icon(designerIcon, { size: 'xs' }), i18n('header.title'), TITLE_ID);
  }

  override connectedCallback() {
    super.connectedCallback();
    // add the popup parent marker class so that select dropdowns are contained in this panel,
    // and so avoid deactivating the view when clicking on them (in particular disabled options).
    this.classList.add(COMPONENT_NAME, POPUP_PARENT_MARKER_CLASS);
    this.setAttribute('role', 'region');
    this.setAttribute('aria-labelledby', TITLE_ID);
    this.dataset.testid = 'view-settings-panel';

    this.#teardowns.push(on(this.workbook, COMMAND_UPDATE, () => this.#onCommandExecuted()));

    observeStore(getStore(), getInitializationStatus, (status) => {
      if (status === 'succeeded') {
        this.#teardowns.push(
          observeStore(
            getStore(),
            (state) => {
              return {
                viewId: getActiveViewId(state),
                viewType: getActiveViewType(state),
                visible: isPanelVisible(state, WorkbookPanelName.VIEW_SETTINGS),
              };
            },
            () => {
              this.updatePanelContent();
            },
            true,
          ),
        );
      }
    });
  }

  disconnectedCallback() {
    this.#teardowns = tearDown(this.#teardowns);
  }

  updatePanelContent(showWorksheetSettings?: boolean) {
    // Remove current content
    const alert = this.querySelector('d-alert');
    if (alert) {
      alert.remove();
    }
    this.setContent(undefined);
    this.panelTitle = i18n('header.title');

    const loadPanelContentPromise = this.#loadPanelContent(showWorksheetSettings);
    this.#loadPanelContentPromise = loadPanelContentPromise;
    loadPanelContentPromise
      .then((panelContent) => {
        // Only set content if no new update request came in the meantime
        if (loadPanelContentPromise === this.#loadPanelContentPromise) {
          this.setContent(panelContent);
          this.panelTitle = (panelContent as DesignerSettingsPanelContent).settingsPanelTitle ?? i18n('header.title');
        }
        this.focus();
      })
      .catch((error) => {
        logger.error('Could not create view settings panel content', error);
        this.append(
          h('d-alert', {
            title: i18n('load.exception.title'),
            message: i18n('load.exception.message'),
          }),
        );
      });
  }

  #loadPanelContent(showWorksheetSettings?: boolean) {
    const view = this.workbook.activeView;
    this.#shownViewId = view?.viewId;
    this.#shownViewType = view?.type;
    this.#shownVisualisationId =
      view?.viewId ? getVisualisationKeyByViewId(getStore().getState(), view.viewId) : undefined;

    if (view) {
      return this.#createViewPanelContent(view);
    }
    return Promise.resolve(this.#createNoViewSelectedPanelContent(showWorksheetSettings));
  }

  #createNoViewSelectedPanelContent(showWorksheetSettings?: boolean) {
    let contentPanel;
    if (showWorksheetSettings && isWorkbookFeatureAvailable(getStore().getState(), 'workbook-management')) {
      contentPanel = new WorksheetSettingsPanel();
    } else {
      contentPanel = new WorksheetTocPanel();
      contentPanel.updatePanelContentCallback = () => this.updatePanelContent(true);
    }
    return contentPanel;
  }

  #createViewPanelContent(view: View): Promise<DesignerSettingsPanelContent> {
    const provider = CONTENT_PROVIDERS_BY_VIEW_TYPE.get(view.type);
    if (!provider) {
      throw new Error(`No view panel content provider found for view type '${view.type}'`);
    }
    return provider(view).then(() => {
      return h<DesignerSettingsPanelContent>(getComponentName(view), { view });
    });
  }

  #onCommandExecuted() {
    const view = this.workbook.activeView;
    const viewIdChanged = this.#shownViewId !== view?.viewId;
    const viewTypeChanged = this.#shownViewType !== view?.type;
    // As extension may have different bundles, so we want to update them if we change from one to another
    const extensionKeyChanged =
      view?.type === 'EXTENSION' &&
      view.viewId &&
      this.#shownVisualisationId !== getVisualisationKeyByViewId(getStore().getState(), view.viewId);
    const isWorksheetOnDemand = isActiveWorksheetOnDemand(getStore().getState());

    if (!isWorksheetOnDemand && (viewIdChanged || viewTypeChanged || extensionKeyChanged)) {
      this.updatePanelContent();
    }
  }
}

customElements.define(COMPONENT_NAME, ViewSettingsPanel);

function getComponentName(view: View) {
  switch (view.type) {
    case 'TEXT':
      return isTextWithDataView(view) ?
          'd-text-with-data-view-settings-panel-content'
        : 'd-text-view-settings-panel-content';
    case 'REPORT':
      return 'd-report-view-settings';
    case 'TABLE':
      return 'd-table-view-settings-panel-content';
    case 'EXTENSION':
      return 'd-extension-view-settings-panel-content';
    case 'IMAGE':
      return 'd-image-view-settings';
    case 'CHART':
      return 'd-chart-view-settings-panel-content';
    case 'OTHER':
      return 'd-indicator-view-settings-panel-content';
    case 'CONTROLLER':
      return 'd-controller-view-settings-panel-content';
    case 'WEBPAGE':
      return 'd-webpage-view-settings-panel-content';
    case 'MAP':
      return 'd-map-view-settings-panel-content';
  }
  assertUnreachable(view.type);
}
